<template>
  <div data-testid="sso-connections-panel">
    <div class="d-flex align-center mb-2">
      <div class="subtitle-1" data-testid="sso-connections-panel__title">SSO connections</div>
      <v-spacer />
      <v-btn
        v-if="editable"
        small
        color="primary"
        class="primaryBtn"
        :disabled="saving || !hostname"
        data-testid="sso-connections-panel__add"
        @click="openCreate"
      >
        <v-icon left small>mdi-plus</v-icon>
        Add connection
      </v-btn>
    </div>

    <v-alert
      v-if="!editable"
      type="info"
      text
      dense
      border="left"
      class="mb-3"
      data-testid="sso-connections-panel__readonly-alert"
    >
      Shared IdP connections are managed on the Consark Development SSO tab
      (app-host platform client).
    </v-alert>

    <div
      v-else
      class="caption grey--text mb-3"
      data-testid="sso-connections-panel__hostname"
    >
      Connections for hostname <code>{{ hostname || '—' }}</code>
    </div>

    <div v-if="(connections || []).length" data-testid="sso-connections-panel__list">
      <div
        v-for="conn in connections"
        :key="conn._id || conn.publicConnectionId || conn.id"
        class="hoverable"
        :data-testid="`sso-connections-panel__row--${connectionId(conn)}`"
      >
        <div class="d-flex align-center py-2 px-2">
          <div>
            <div class="font-weight-bold">
              {{ conn.displayName || conn.name || 'Connection' }}
            </div>
            <div class="caption grey--text">
              {{ conn.protocol || 'SAML' }}
              · {{ conn.lifecycle || 'DRAFT' }}
              <span v-if="conn.publicConnectionId">
                · {{ conn.publicConnectionId }}
              </span>
              <span v-if="conn.cognitoProviderName">
                · {{ conn.cognitoProviderName }}
              </span>
            </div>
          </div>
          <v-spacer />
          <v-chip
            small
            label
            class="mr-2"
            :color="lifecycleColor(conn.lifecycle)"
            :data-testid="`sso-connections-panel__lifecycle--${connectionId(conn)}`"
          >
            {{ conn.lifecycle || 'DRAFT' }}
          </v-chip>
          <template v-if="editable && conn.lifecycle !== 'RETIRED'">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  :disabled="saving"
                  :data-testid="`sso-connections-panel__edit--${connectionId(conn)}`"
                  @click="openEdit(conn)"
                >
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-btn>
              </template>
              <span>Edit</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  :disabled="saving"
                  :data-testid="`sso-connections-panel__test--${connectionId(conn)}`"
                  @click="run('test', conn)"
                >
                  <v-icon small>mdi-flask-outline</v-icon>
                </v-btn>
              </template>
              <span>Test</span>
            </v-tooltip>
            <v-tooltip bottom v-if="conn.lifecycle !== 'ACTIVE'">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  :disabled="saving"
                  :data-testid="`sso-connections-panel__activate--${connectionId(conn)}`"
                  @click="run('activate', conn)"
                >
                  <v-icon small>mdi-check</v-icon>
                </v-btn>
              </template>
              <span>Activate</span>
            </v-tooltip>
            <v-tooltip bottom v-if="conn.lifecycle === 'ACTIVE'">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  :disabled="saving"
                  :data-testid="`sso-connections-panel__disable--${connectionId(conn)}`"
                  @click="run('disable', conn)"
                >
                  <v-icon small>mdi-pause</v-icon>
                </v-btn>
              </template>
              <span>Disable</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  v-bind="attrs"
                  v-on="on"
                  :disabled="saving"
                  :data-testid="`sso-connections-panel__retire--${connectionId(conn)}`"
                  @click="askRetire(conn)"
                >
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </template>
              <span>Retire</span>
            </v-tooltip>
          </template>
        </div>
        <v-divider />
      </div>
    </div>
    <div
      v-else-if="editable"
      class="title grey--text my-4 text-center"
      data-testid="sso-connections-panel__empty"
    >
      No connections for this hostname
    </div>

    <ConnectionFormDialog
      v-model="showForm"
      :hostname="hostname"
      :loading="saving"
      :connection="editTarget"
      @submit="onSubmit"
    />

    <RetireConfirmDialog
      v-model="retireDialog"
      title="Retire connection"
      message="Retired connections are removed from login options."
      :loading="saving"
      @confirm="confirmRetire"
    />
  </div>
</template>

<script>
import ConnectionFormDialog from './ConnectionFormDialog.vue';
import RetireConfirmDialog from './RetireConfirmDialog.vue';

export default {
  name: 'ConnectionsPanel',
  components: { ConnectionFormDialog, RetireConfirmDialog },
  props: {
    connections: { type: Array, default: () => [] },
    hostname: { type: String, default: '' },
    editable: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      showForm: false,
      editTarget: null,
      retireDialog: false,
      retireTarget: null,
    };
  },
  methods: {
    lifecycleColor(lifecycle) {
      switch (lifecycle) {
        case 'ACTIVE':
          return 'success';
        case 'TESTING':
          return 'info';
        case 'DISABLED':
          return 'warning';
        case 'RETIRED':
          return 'grey';
        default:
          return 'blue-grey lighten-4';
      }
    },
    connectionId(conn) {
      return conn?.publicConnectionId || conn?._id || conn?.id;
    },
    openCreate() {
      this.editTarget = null;
      this.showForm = true;
    },
    openEdit(conn) {
      this.editTarget = conn;
      this.showForm = true;
    },
    run(action, conn) {
      const id = this.connectionId(conn);
      if (!id) return;
      this.$emit(action, { connectionId: id });
    },
    onSubmit(payload) {
      if (this.editTarget) {
        this.$emit('update', {
          connectionId: this.connectionId(this.editTarget),
          payload,
        });
      } else {
        this.$emit('create', payload);
      }
      this.showForm = false;
      this.editTarget = null;
    },
    askRetire(conn) {
      this.retireTarget = conn;
      this.retireDialog = true;
    },
    confirmRetire({ reason, confirm }) {
      const id = this.connectionId(this.retireTarget);
      if (!id) return;
      this.$emit('retire', { connectionId: id, reason, confirm });
      this.retireDialog = false;
      this.retireTarget = null;
    },
  },
};
</script>
